import './LoginForm.less';

import { Form, Input } from 'antd';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { LoginForm } from 'domains/appDomain';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import accountService from 'services/accountService';
import { canVisitAdminSpace, isSourcer, isSupplier } from 'utils/rbac';
import { RULE_TEXT_LEN_255 } from 'utils/validators';
import appService, { OAUTH_REDIRECT_SENTINEL } from 'services/appService';
import { useHistory } from 'react-router-dom';

export const LoginFormComponent = (props: { setStep: Function }) => {
  const { setStep } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isSubmitting = useAppSelector(
    (state) => state.loading.effects.app.login
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const themeData = useAppSelector((state) => state.app.themeData);
  const { token, tokenType } = useAppSelector((state) => state.account);

  const [form] = Form.useForm();

  // Check if current domain should show SSO login
  const shouldShowSsoLogin = window.location.hostname.endsWith(
    'albertsonsprintcenter.com'
  );

  const onLogin = async (loginForm: LoginForm) => {
    try {
      if (token && tokenType === 'VERIFY') {
        await accountService.verify(token);
        dispatch.account.setToken({ token: undefined, tokenType: undefined });
      }

      loginForm.username = loginForm.username.trim();
      const user = await dispatch.app.login({
        ...loginForm,
      });
      if (user === OAUTH_REDIRECT_SENTINEL) {
        return; // OAuth redirect in progress; do not run pathname logic
      }
      // if (loginRedirect) {
      //   window.location.pathname = '/' + loginRedirect;
      //   dispatch.account.setRedirect(undefined);
      // } else
      if (isSupplier(user) || isSourcer(user)) {
        window.location.pathname = '/workspace';
      } else if (canVisitAdminSpace(user)) {
        window.location.pathname = '/platform/org';
      } else {
        window.location.pathname = '/';
      }
    } catch {}
  };

  const onSsoLogin = async () => {
    const ssoUrl = await appService.ssoInit();
    window.location.href = ssoUrl;
  };

  const onValuesChange = (
    changedValues: { username?: string },
    allValues: { username?: string; password?: string }
  ) => {
    setUsername(allValues.username || '');
    setPassword(allValues.password || '');
  };

  return (
    <div className={`login-form ${shouldShowSsoLogin ? 'compact' : ''}`}>
      <h1 className="login-form-title">{t('login.loginForm.title')}</h1>
      
      {shouldShowSsoLogin && (
        <>
          <div style={{ width: '100%', marginBottom: '24px' }}>
            <RevxButton
              onClick={onSsoLogin}
              type="primary"
              style={{ width: '100%', height: '46px', fontSize: '16px', fontWeight: 600 }}
            >
              ACI SSO LOG IN
            </RevxButton>
          </div>
          <div className="alternate-login-divider"></div>
          <div className="divider-text">
            <div>Alternate login</div>
            <div className="small-text">(requires existing account)</div>
          </div>
        </>
      )}

      <RevxSpin spinning={isSubmitting}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onLogin}
          onValuesChange={onValuesChange}
        >
          <Form.Item className="form-item-username">
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('login.loginForm.usernameRequired'),
                },
                RULE_TEXT_LEN_255,
              ]}
              name="username"
              label={t('login.loginForm.usernameLabel')}
              className={!username ? 'hide-label' : ''}
            >
              <Input
                autoFocus
                placeholder={t('login.loginForm.usernamePlaceholder')}
              />
            </Form.Item>
            {username && (
              <CheckIcon className="check-icon" fill={themeData.primaryColor} />
            )}
          </Form.Item>
          <Form.Item className="form-item-username">
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('login.loginForm.passwordRequired'),
                },
                RULE_TEXT_LEN_255,
              ]}
              name="password"
              label={t('login.loginForm.passwordLabel')}
              className={!password ? 'hide-label' : ''}
            >
              <Input.Password
                type="password"
                placeholder={t('login.loginForm.passwordPlaceholder')}
              />
            </Form.Item>
          </Form.Item>
          <Form.Item className="form-item-button">
            <RevxButton
              size="large"
              htmlType="submit"
              type="primary"
              className="login-form-button"
            >
              {t('login.loginForm.submit')}
            </RevxButton>

            <RevxFlexLayout justify="space-between" className="login-action">
              {shouldShowSsoLogin ? (
                <div></div>
              ) : (
                <RevxButton
                  type="link"
                  className="forgot-password"
                  data-testid="button-email-me-a-code"
                  onClick={() => {
                    history.push('/login/otp');
                  }}
                >
                  Email Me a Code
                </RevxButton>
              )}
              <RevxButton
                type="link"
                className="forgot-password"
                onClick={() => {
                  setStep('forgot');
                }}
              >
                Forgot Password?
              </RevxButton>
            </RevxFlexLayout>
          </Form.Item>
        </Form>
      </RevxSpin>
    </div>
  );
};
